import { StorageManager } from '../../../../../../common/storage-manager.class';
import {
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { environment } from '../../../../../../../environments/environment';
import { SubcontratoModel } from '../../../../../../models/tareas/subcontrato.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SubcontratosListsDefinition } from './list-variables';
import { getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';

export class SubcontratosFormDefinition {
    public lists: SubcontratosListsDefinition = new SubcontratosListsDefinition();
    public formType!: FormType;
    public model: Partial<SubcontratoModel> = {};
    public formFields!: Form<SubcontratoModel>;
    public fieldNames: FormField<SubcontratoModel>[] = [];
    public fieldLabels: string[] = [];

    public readonly appName = environment.appName;
    public readonly fincaName = environment.features.fincaName;
    public readonly hasClients = environment.features.hasClients;
    public readonly serverUrl = environment.serverUrl;

    private readonly user = StorageManager.getUser();
    private readonly client = StorageManager.getClient();

    constructor(type: FormType, model: Partial<SubcontratoModel>) {
        this.formType = +type;
        this.model = model;
        this.formFields = {
            name: 'subcontratos',
            header: 'Datos de Subcontratos',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<SubcontratoModel>[] {
        const sections: FormSection<SubcontratoModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<SubcontratoModel>[] {
        const fields: FormField<SubcontratoModel>[] = [
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    defaultValue: this.formType === FormType.CREATION ? new Date() : null,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'id_finca',
                vinculatedFields: undefined,
                label: this.fincaName,
                input: {
                    type: this.formType > FormType.DUPLICATION ? InputType.TEXT : InputType.DROPDOWN,
                    data: this.lists.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true, isVisible: this.formType <= FormType.DUPLICATION }),
                order: 2,
            },
            {
                name: 'finca',
                vinculatedFields: undefined,
                label: this.fincaName,
                input: {
                    type: InputType.TEXT,
                    isEditable: false,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.formType > FormType.DUPLICATION }),
                order: 2,
            },
            {
                name: 'proveedor',
                vinculatedFields: undefined,
                label: 'Proveedor',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'concepto',
                vinculatedFields: undefined,
                label: 'Concepto',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 4,
            },
            {
                name: 'coste',
                vinculatedFields: undefined,
                label: 'Coste',
                input: {
                    type: InputType.NUMERIC,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 5,
            },
        ];

        this.registerVisibleFields(fields);

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<SubcontratoModel>[] {
        const fields: FormField<SubcontratoModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id ?? null,
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true, isRememberedOnDuplicate: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getInitialClientId(),
                    isEditable: false,
                },
                width: 1,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: () => this.resolveClientId(),
                },
                updateValue: () => this.resolveClientId(),
                conditions: this.conditions({ isVisible: this.hasClients }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getDefaultUserId(),
                    isEditable: false,
                },
                width: 1,
                computed: {
                    resolve: () => this.getDefaultUserId(),
                },
                updateValue: () => this.getDefaultUserId(),
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
            {
                name: 'ids_trabajadores',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getDefaultWorkerId(),
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true }),
                order: 4,
            },
            {
                name: 'prioridad',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.prioridad ?? 'No-Realizada',
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true }),
                order: 5,
            },
            {
                name: 'mantenimiento',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.mantenimiento ?? 0,
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true }),
                order: 6,
            },
        ];

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private registerVisibleFields(fields: FormField<SubcontratoModel>[]): void {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);
        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }

    private conditions(options: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: options.isRememberedOnDuplicate ?? true,
            isRequired: options.isRequired ?? false,
            isRetained: options.isRetained ?? false,
            hasUndoEnabled: options.hasUndoEnabled ?? false,
            isVisible: options.isVisible ?? true,
        };
    }

    private getInitialClientId(): string | null {
        return this.client?.id?.toString() ?? this.resolveClientId() ?? null;
    }

    private resolveClientId(): string | null {
        const finca = getDropdownSelectedSingle(this.lists.fincas) as FincasModel | null;
        const clientId = finca?.id_cliente ?? this.model.id_cliente ?? this.client?.id;

        return clientId !== undefined && clientId !== null ? String(clientId) : null;
    }

    private getDefaultUserId(): string | null {
        const modelUserId = this.model.id_usuario;

        if (modelUserId !== undefined && modelUserId !== null && String(modelUserId).trim().length > 0) {
            return String(modelUserId);
        }

        return this.user?.id !== undefined && this.user.id !== null ? String(this.user.id) : null;
    }

    private getDefaultWorkerId(): string | null {
        if (this.model.ids_trabajadores !== undefined && this.model.ids_trabajadores !== null) {
            return String(this.model.ids_trabajadores);
        }

        if (this.user?.rol !== 'user') {
            return null;
        }

        return this.user?.id !== undefined && this.user.id !== null ? String(this.user.id) : null;
    }
}
